import _isNil from 'lodash/isNil';
import { DateTime } from 'luxon';
import React, { useMemo, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';

import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';

import { accountsSelectors } from 'app-components/flux/accounts';
import { customFrequencyUtils } from 'app-components/flux/scheduled-transactions';
import type { RecurrenceType } from 'app-components/flux/scheduled-transactions/customFrequencyTypes';
import { isStateEqualProps } from 'app-components/flux/scheduled-transactions/customFrequencyUtils';

import AmountSpan from 'components/QuickenControls/AmountField/AmountSpan';

import EveryQuarter from './EveryQuarter';
import EveryXDays from './EveryXDays';
import EveryXMonths from './EveryXMonths';
import EveryXWeeks from './EveryXWeeks';
import EveryXYears from './EveryXYears';
import Monthly from './Monthly';
import MultipleDates from './MultipleDates';
import OneTime from './OneTime';
import TwiceAMonth from './TwiceAMonth';
import Weekly from './Weekly';
import Yearly from './Yearly';
import { FrequencyContext } from './state';

import { convertClientAliasToQCSAlias } from 'app-components/flux/scheduled-transactions/customFrequencyUtils';
import type { Frequency } from 'app-components/flux/scheduled-transactions/frequency';
import { AliasEnum } from 'app-components/flux/scheduled-transactions/frequency';
import { makeStyles } from 'tss-react/mui';

const { recurrenceReducer, recurrenceInfo } = customFrequencyUtils;

const useStyles = makeStyles()((theme) => ({
  freqTitle: {
    display: 'flex',
    flex: 0,
    fontWeight: 500,
    paddingBottom: 8,
    paddingRight: 8,
  },
  textSummary: {
    display: 'block',
    flex: 1,
    padding: 16,
    borderRadius: 12,
    backgroundColor: theme.palette.info.light,
    fontSize: 12,
    marginTop: theme.spacing(3),
  },
  text: {
    paddingRight: 3,
    color: theme.palette.text.tertiary,
  },
  typeSelectorBtnRoot: {
    background: theme.palette.background.default,
    width: '100%',
    borderRadius: 12,
    flexDirection: 'row',
    display: 'flex',
    justifyContent: 'space-between',
  },
  expansionHeader: {
    display: 'block',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    flexWrap: 'nowrap',
    background: theme.palette.background.paper,
    borderRadius: 12,
  },
  titleIconGroup: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: 8,
  },
  rowRoot: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: 10,
    margin: 8,
  },
  accountField: {
    color: theme.palette.text.secondary,
    width: 210,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    textAlign: 'left',
  },
  standardWidth: {
    width: 110,
    textAlign: 'left',
  },
  openIcon: {
    color: theme.palette.icon.light,
    marginRight: 12,
  },
  closedIcon: {
    color: theme.palette.icon.main,
    marginRight: 12,
  },
  preview: {
    width: '100%',
    textAlign: 'left',
  },
  showOnly: {
    width: '100%',
    textAlign: 'right',
  },
  amountWidth: {
    minWidth: 60,
  },
  disabledText: {
    color: theme.palette.text.disabled,
  },
}));

/* Notes:
dueOn, until, and alias are managed by the parent Formik form.  These values are passed to the CustomFrequency component which incorporates 
these 3 values into the recurrence and sets other recurrence values based on these values. 
The challenge here is to keep these values in sync between the recurrence instance (stored in the context) and the Formik form.
*/

function CustomFrequency({
  initialState,
  currentDueOn,
  currentUntil,
  currentAlias,
  currentFrequency,
  accountId,
  amount,
  error,
  onChange, // TODO: broken
  onDateChange,
}: {
  initialState: RecurrenceType;
  currentDueOn: DateTime;
  currentUntil?: DateTime;
  currentAlias: string;
  currentFrequency: Frequency;
  accountId: string;
  amount: number;
  error: boolean;
  onChange: (recurrence: RecurrenceType) => void;
  onDateChange: (date: DateTime) => void;
}) {
  const { classes } = useStyles();

  const [expanded, setExpanded] = useState(false);
  const accountInfo = useSelector((state: any) => accountsSelectors.getAccountById(state, accountId));

  const [contextState, contextDispatch] = useReducer(recurrenceReducer, initialState);

  const stateContextValue = useMemo(
    () => ({
      state: contextState,
      dispatch: contextDispatch,
      error,
    }),
    [contextState, contextDispatch, error],
  );

  // get summary text and next dates for recurrence
  const { nextDates, text: rruleText } = useMemo(
    () => recurrenceInfo({ recurrence: contextState, startDate: currentDueOn, endDate: currentUntil }),
    [currentDueOn, currentUntil, contextState],
  );

  // retnder the correct frequency component
  const frequencyContent = useMemo(() => {
    // set the due on date based on the recurrence or today in the case that the contextState.dueOn is not already set to that date
    let nextDate: DateTime | undefined;
    if (contextState.alias === AliasEnum.ONE_TIME_PAYMENT) {
      nextDate = contextState.dueOn;
    } else {
      nextDate = nextDates[0] ? DateTime.fromJSDate(nextDates[0]) : currentDueOn;
    }
    nextDate = nextDate?.startOf('day') || DateTime.local().startOf('day');

    if (!contextState.dueOn?.equals(nextDate)) {
      contextDispatch({ type: 'UPDATE_DUE_ON', payload: nextDate });
      return;
    }

    // user changed something
    if (currentAlias !== contextState.alias) {
      contextDispatch({
        type: 'UPDATE_DEFAULT',
        payload: { alias: currentAlias, dueOn: currentDueOn, endOn: currentUntil },
      });
    }

    // When initially loaded, isValid may not have been evaluated
    if (_isNil(contextState.isValid)) {
      contextDispatch({
        type: 'VALIDATE_RECURRENCE',
        payload: undefined,
      });
    }

    // FIXME: causes infinite re-renders
    if (!isStateEqualProps(contextState, initialState)) {
      onChange(contextState);
    }

    // at this point, we are assured that the start date and alias are in sync. The isValid is set corretly. W
    // We can invoke the apppropriate frequency display component.
    let content;
    const componentAlias = convertClientAliasToQCSAlias(currentAlias);

    switch (componentAlias) {
      case AliasEnum.ONE_TIME_PAYMENT:
        content = <OneTime onDateChange={onDateChange} />;
        break;
      case AliasEnum.EVERY_WEEK:
        content = <Weekly />;
        break;
      case AliasEnum.EVERY_MONTH:
        content = <Monthly />;
        break;
      case AliasEnum.EVERY_YEAR:
        content = <Yearly />;
        break;
      case AliasEnum.EVERY_QUARTER:
        content = <EveryQuarter />;
        break;
      case AliasEnum.EVERY_X_DAYS:
        content = <EveryXDays />;
        break;
      case AliasEnum.EVERY_X_WEEKS:
        content = <EveryXWeeks />;
        break;
      case AliasEnum.EVERY_X_MONTHS:
        content = <EveryXMonths />;
        break;
      case AliasEnum.EVERY_X_YEARS:
        content = <EveryXYears />;
        break;
      case AliasEnum.TWICE_A_MONTH:
        content = <TwiceAMonth />;
        break;
      case AliasEnum.MULTIPLE_FIXED_DATES:
        content = <MultipleDates />;
        break;
      default:
        content = <Monthly />;
    }

    return (
      <>
        {!currentAlias.startsWith('Every X') && <Typography className={classes.freqTitle}>{currentAlias}</Typography>}
        {content}
      </>
    );
  }, [
    contextState,
    currentAlias,
    initialState,
    classes.freqTitle,
    nextDates,
    currentDueOn,
    currentUntil,
    onChange,
    onDateChange,
  ]);

  // render the preview component for valid recurrences
  const previewContent = useMemo(() => {
    const { isValid } = contextState;
    const showNextOccurrence =
      currentAlias !== 'One-time payment' && currentAlias !== 'Multiple fixed dates' && isValid;
    const accountName = accountInfo?.name || <span className={classes.disabledText}>Specify account</span>;

    return (
      <Box>
        <div className={classes.textSummary}>
          <span className={classes.text}>{rruleText}</span>
          {showNextOccurrence && (
            <ButtonBase
              onClick={() => {
                setExpanded(!expanded);
              }}
              className={classes.typeSelectorBtnRoot}
              id={'preview-button'}
            >
              <div className={classes.expansionHeader}>
                <div className={classes.titleIconGroup}>
                  {expanded ? (
                    <ArrowDropDownRoundedIcon className={classes.openIcon} />
                  ) : (
                    <ArrowRightRoundedIcon className={classes.closedIcon} />
                  )}
                  <Typography variant='bodyRegular' className={classes.preview}>
                    Preview Next Occurrence
                  </Typography>
                  {expanded && nextDates && (
                    <Typography variant='captionRegular' className={classes.showOnly}>
                      {`Showing next ${nextDates?.length} only`}
                    </Typography>
                  )}
                </div>
                {expanded &&
                  nextDates &&
                  nextDates.map((date, index) => {
                    const nextDate = date.toLocaleDateString('en-us', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    });
                    return (
                      <div key={`${nextDate}`} id={`${index}-row`} role='presentation' className={classes.rowRoot}>
                        <Typography id={`${index}-date`} variant='bodyRegular' className={classes.standardWidth}>
                          {nextDate}
                        </Typography>
                        <Typography id={`${index}-account`} className={classes.accountField}>
                          {accountName}
                        </Typography>
                        <div id={`${index}-amount`} className={classes.amountWidth}>
                          <AmountSpan
                            initialValue={Number(amount ? amount : 0)}
                            variant='bodyRegular'
                            editable={false}
                            showSign={false}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </ButtonBase>
          )}
        </div>
      </Box>
    );
  }, [contextState, currentAlias, accountInfo?.name, classes, rruleText, expanded, nextDates, amount]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, pl: 2 }}>
      {currentFrequency && (
        <Box sx={{ display: 'flex', flex: 0, flexDirection: 'column' }}>
          <FrequencyContext.Provider value={stateContextValue}>{frequencyContent}</FrequencyContext.Provider>
        </Box>
      )}

      {rruleText && contextState.isValid && previewContent}
    </Box>
  );
}

export default CustomFrequency;
